import { GlobalHeaderProps, ProfileProps } from '@design-system/global-header';
import { NavigationDataItem } from '@design-system/global-header/dist/types/components/navigation/models';
import {
  LogoDataProps,
  SkipLinkProps,
  UtilityLinksDataItem,
} from '@design-system/global-header/dist/types/models';
import { navigate } from 'gatsby';
import React from 'react';

import Splitter from '~/components/Splitter';
import TMPLinkWrap from '~/components/TMPLinkWrap';
import { UseLanguageSwitchHook } from '~/hooks/useLanguageSwitch';
import { buttonMapper, buttonWithLinkMapper } from '~/mappers/button';
import { iconMapper } from '~/mappers/common';
import { linkBaseMapper, linkMapper } from '~/mappers/link';
import { LinkBase } from '~/mappers/link/models';

import { imageMapper } from '../image';
import {
  LAST_SEARCHES_QUANTITY,
  LOCAL_STORAGE_LAST_SEARCHES_KEY,
} from './constants';
import {
  HeaderBasic,
  HeaderCommon,
  HeaderSplitterAdditionalData,
  HeaderWithSplitter,
  Logo,
  MapHeaderItemsResult,
  NavigationLink,
  NavigationLinkWithSplitter,
  Profile,
  SearchBlock,
  SearchBlockProps,
  SkipLink,
} from './models';

const headerLogoMapper = ({
  smallImage,
  largeImage,
  link,
  alt,
}: Logo): LogoDataProps => ({
  imageSmall: imageMapper({ image: smallImage, alt }, true)!,
  imageLarge: imageMapper({ image: largeImage, alt }, true)!,
  ...{ link: { component: TMPLinkWrap, props: linkMapper(link) } },
});

const headerItemsMapper = (
  item: NavigationLink | LinkBase
): MapHeaderItemsResult => {
  const { href, title: label, ariaLabel } = linkBaseMapper(item);

  return {
    label,
    href: href as string,
    ariaLabel: ariaLabel || label,
    ...('submenuLabel' in item && { subMenuLabel: item.submenuLabel }),
    ...('submenu' in item &&
      item.submenu.length && { subItems: item.submenu.map(headerItemsMapper) }),
  };
};

const headerWithSplitterItemsMapper = (
  item: NavigationLinkWithSplitter | LinkBase,
  headerSplitterAdditionalData?: HeaderSplitterAdditionalData
): MapHeaderItemsResult => {
  const { href, title: label, ariaLabel } = linkBaseMapper(item);

  return {
    label,
    href: href as string,
    ariaLabel: ariaLabel || label,
    ...('submenu' in item &&
      item.submenu?.[0] && {
        submenu: (
          <Splitter {...item.submenu[0]} {...headerSplitterAdditionalData} />
        ),
      }),
  };
};

const skipLinkMapper = ({ mode, link }: SkipLink): SkipLinkProps => ({
  ...linkMapper(link),
  mode,
});

const searchMapper = (search: SearchBlock[]): SearchBlockProps | undefined => {
  if (!search[0]) return undefined;

  const [
    {
      searchLabel,
      searchInputPlaceholder,
      ariaLabelOpen,
      ariaLabelClose,
      swipeLabel,
      lastSearchesTitle,
      searchButtonText,
      searchResultsLink,
    },
  ] = search;

  return {
    searchLabel,
    searchInputPlaceholder,
    ariaLabelOpen,
    ariaLabelClose,
    swipeLabel,
    localStorageLastSearchesKey: LOCAL_STORAGE_LAST_SEARCHES_KEY,
    lastSearchesQuantity: LAST_SEARCHES_QUANTITY,
    lastSearchesTitle,
    searchButtonText,
    customNavigate: (searchText) => {
      navigate(`${searchResultsLink[0].url}${searchText}`);
    },
    searchPageUrl: `${searchResultsLink[0].url}`,
  };
};

const languageSwitchMapper = (
  languageSwitch: ReturnType<UseLanguageSwitchHook>
) => {
  if (languageSwitch.list.length < 2) return undefined;

  const currLangSwitchSettings = languageSwitch.list.find(
    ({ currentPage }) => currentPage
  )!.languageSwitchSettings;

  return {
    title: currLangSwitchSettings.languageName,
    ariaLabel: currLangSwitchSettings.navButtonAria,
    languageData: languageSwitch.list
      .filter(({ currentPage }) => !currentPage)
      .map((item) => ({
        label: item.translated
          ? item.languageSwitchSettings.languageName
          : item.languageSwitchSettings.missingTranslation,
        href: item.url,
        locale: item.locale,
        translated: item.translated,
      })),
  };
};

const profileMapper = ({
  headline,
  greeting,
  icon,
  account,
  register,
  logIn,
  logOut,
}: Profile) => ({
  headline,
  greeting,
  icon: iconMapper(icon[0]),
  account: linkMapper(account),
  register: buttonWithLinkMapper(register),
  logIn: buttonWithLinkMapper(logIn),
  logOut: buttonMapper(logOut),
  linkNavElement: TMPLinkWrap,
});

export const headerCommonMapper = ({
  logo,
  burger: {
    openIcon: [burgerOpenIcon],
    closeIcon: [burgerCloseIcon],
    toggleLabel: burgerToggleLabel,
    closeLabel: burgerCloseLabel,
    openLabel: burgerOpenLabel,
  },
  moreButton: moreBtn,
  navigationAriaLabel,
  navigationCloseIcon: closeIcon,
  navigationOpenIcon: openIcon,
  isHeaderSticky,
  mode,
  skipLink,
  languageSwitch,
  search,
  uid,
  profile,
  maxVisibleNavItems,
}: HeaderCommon) => ({
  mode,
  logo: headerLogoMapper(logo),
  burgerOpenIcon: iconMapper(burgerOpenIcon),
  burgerCloseIcon: iconMapper(burgerCloseIcon),
  burgerToggleLabel,
  burgerCloseLabel,
  burgerOpenLabel,
  navigationName: navigationAriaLabel,
  activeUrlPath:
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.hash.replace('/', '')}`
      : '',
  moreBtn,
  ...(openIcon?.length && { openIcon: iconMapper(openIcon[0]) }),
  ...(closeIcon?.length && { closeIcon: iconMapper(closeIcon[0]) }),
  isHeaderSticky,
  maxVisibleNavItems,
  skipLink: skipLinkMapper(skipLink),
  linkNavElement: TMPLinkWrap,
  languageSwitch: languageSwitchMapper(languageSwitch),
  search: searchMapper(search),
  uid,
  profile: profile
    ? (profileMapper(profile) as unknown as ProfileProps)
    : undefined,
});

export const headerBasicMapper = ({
  navigation,
  utilityLinks,
  utilityLinksName,
  ...rest
}: HeaderBasic): GlobalHeaderProps => ({
  ...headerCommonMapper(rest),
  navigationData: navigation
    .map(headerItemsMapper)
    .filter((val) => Boolean(val)) as NavigationDataItem[],
  utilityLinksName,
  ...(utilityLinks?.length && {
    utilityLinksData: utilityLinks
      .map(headerItemsMapper)
      .filter((val) => Boolean(val)) as UtilityLinksDataItem[],
  }),
});

export const headerWithSplitterMapper = (
  { navigation, ...rest }: HeaderWithSplitter,
  headerSplitterAdditionalData?: HeaderSplitterAdditionalData
): GlobalHeaderProps => ({
  ...headerCommonMapper(rest),
  navigationData: navigation
    .map((item) =>
      headerWithSplitterItemsMapper(item, headerSplitterAdditionalData)
    )
    .filter((val) => Boolean(val)) as NavigationDataItem[],
});

export const headerMapper = ({
  headerBasic,
  headerWithSplitter,
  headerSplitterAdditionalData,
  languageSwitch,
  profile,
}: {
  headerBasic?: HeaderBasic | null;
  headerWithSplitter?: HeaderWithSplitter | null;
  headerSplitterAdditionalData?: HeaderSplitterAdditionalData;
  languageSwitch: ReturnType<UseLanguageSwitchHook>;
  profile?: Profile;
}) => {
  if (headerWithSplitter)
    return headerWithSplitterMapper(
      {
        ...headerWithSplitter,
        languageSwitch,
        profile,
      },
      headerSplitterAdditionalData
    );

  if (headerBasic)
    return headerBasicMapper({ ...headerBasic, languageSwitch, profile });

  return null;
};
